import i18n from 'i18next';
import { bcp47For } from './negotiate';

/**
 * `Intl` helpers for every date, money and relative-time site (#2109 R1
 * lane L2; research §2.2 rules 9–12, 15).
 *
 * The formatting locale is the ACTIVE catalog’s `bcp47` tag — never an
 * `'en-US'` literal — so `pt` formats as pt-BR even when the browser says
 * `pt-PT`. `hour12`/`hourCycle` are never passed (each locale keeps its native
 * cycle); `timeZone` is always explicit; date-only values format in UTC
 * (B-432). Money never goes through `toFixed(2)`; `Intl.NumberFormat` with
 * the default `symbol` display gives `US$` outside en-US (rulings brief B15).
 *
 * Tests assert with `formatToParts` or a regex, never exact strings — Node 24
 * renders `22 sept 2026` for es and a narrow no-break space in fr.
 */

function activeBcp47(locale?: string): string {
  return bcp47For(locale ?? i18n.resolvedLanguage ?? i18n.language ?? 'en');
}

/** The runtime’s own zone — the explicit `timeZone` when a caller has no better one. */
export function localTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export interface FormatDateOptions {
  dateStyle?: Intl.DateTimeFormatOptions['dateStyle'];
  timeStyle?: Intl.DateTimeFormatOptions['timeStyle'];
  timeZone?: string;
  /** Override the active locale (declaration code); tests use it. */
  locale?: string;
}

function toDate(iso: string | Date): Date {
  return iso instanceof Date ? iso : new Date(iso);
}

/** An instant, in the person’s zone unless `timeZone` says otherwise. */
export function formatDate(iso: string | Date, options: FormatDateOptions = {}): string {
  const { locale, timeZone, dateStyle = 'medium', timeStyle } = options;
  return new Intl.DateTimeFormat(activeBcp47(locale), {
    dateStyle,
    ...(timeStyle ? { timeStyle } : {}),
    timeZone: timeZone ?? localTimeZone(),
  }).format(toDate(iso));
}

/** A date-only value (stored as UTC midnight) — formatted in UTC so it never reads as the previous day (B-432). */
export function formatDateOnly(iso: string | Date, options: Omit<FormatDateOptions, 'timeZone' | 'timeStyle'> = {}): string {
  const { locale, dateStyle = 'medium' } = options;
  return new Intl.DateTimeFormat(activeBcp47(locale), { dateStyle, timeZone: 'UTC' }).format(toDate(iso));
}

/** The two currencies TRF awards in (contract C-1: `US → USD`, `CA → CAD`). */
export type MoneyCurrency = 'USD' | 'CAD';

/**
 * Group every amount, four digits included (#2533). Spanish CLDR sets
 * minimumGroupingDigits 2, so the default `auto` printed "1000 US$" beside
 * "12.000 US$". `'always'` is Intl.NumberFormat v3; an engine without it
 * coerces the string to `true`, which groups as before.
 */
const MONEY_GROUPING = 'always' as unknown as boolean;

/**
 * The locale whose NUMBER shape money takes, per formatting locale, where it
 * differs from the language's own (local stack 2026-10-13).
 *
 * Plain `es` is Spain's CLDR data: "1.000 US$", "342,50 US$". TRF's families
 * live in the US and Canada, and its Spanish speakers come mostly from Mexico
 * and Central America, where an amount reads "US$1,000" / "US$342.50": comma
 * grouping, dot decimals, the symbol first. `es-US` is CLDR's Spanish for
 * exactly that reader. `es-CA` has no CLDR data (it falls back to Spain), and
 * Canada's everyday grouping is the same comma, so a Canadian family takes
 * `es-US` numbers too: "CAD 1,500".
 *
 * Only the number moves. The SYMBOL stays the language's own CLDR symbol
 * (`US$`, `CAD` in es), so rulings brief B15 holds: no bare `$` outside en,
 * whatever `es-US` itself would print for USD.
 *
 * fr (space grouping, "1 000 $US"), pt-BR ("US$ 1.000", ruled the one pt
 * formatting locale) and zh-Hans ("US$1,000") already read the way their
 * readers write money, and en is untouched, so none of them has a row.
 */
const MONEY_NUMBER_LOCALE: Readonly<Record<string, string>> = { es: 'es-US' };

function formatCurrency(
  amount: number,
  currency: MoneyCurrency,
  locale: string | undefined,
  minimumFractionDigits: number,
): string {
  const bcp47 = activeBcp47(locale);
  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits: 2,
    useGrouping: MONEY_GROUPING,
  };
  const numberLocale = MONEY_NUMBER_LOCALE[bcp47];
  if (!numberLocale) return new Intl.NumberFormat(bcp47, options).format(amount);
  const symbol = new Intl.NumberFormat(bcp47, options).formatToParts(0).find((p) => p.type === 'currency')?.value;
  return new Intl.NumberFormat(numberLocale, options)
    .formatToParts(amount)
    .map((p) => (p.type === 'currency' && symbol ? symbol : p.value))
    .join('');
}

/**
 * THE money formatter (#2109 R3-B6). Integer cents in, the active locale’s
 * currency string out: `$1,000` · `$342.50` · `CA$1,500` in en, `US$1,250.50`
 * · `CAD 1,500` in es (`MONEY_NUMBER_LOCALE`). Whole amounts drop `.00`, an
 * amount with cents keeps two decimals,
 * and null / undefined / NaN is `—` — the same contract as `lib/money.ts`
 * `formatMoney`, so no English `$1,000` assertion moves when a caller swaps.
 * DOLLAR fields (`ReimbursementClaim.amount`) convert to cents at ONE
 * boundary (`bills-display.ts` `formatUsd`), never by hand at the call site.
 */
export function formatMoney(
  cents: number | null | undefined,
  currency: MoneyCurrency = 'USD',
  options: { locale?: string } = {},
): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  const amount = cents / 100;
  return formatCurrency(amount, currency, options.locale, Number.isInteger(amount) ? 0 : 2);
}

/** Integer cents → USD with cents always shown (`$1,000.00` / `US$1,000.00` / `1 000,00 $US`). A thin wrapper kept for R1 callers; new code uses `formatMoney`. */
export function formatMoneyCents(cents: number, options: { locale?: string } = {}): string {
  return formatCurrency(cents / 100, 'USD', options.locale, 2);
}

const RELATIVE_UNITS: ReadonlyArray<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 1000 * 60 * 60 * 24 * 365],
  ['month', 1000 * 60 * 60 * 24 * 30],
  ['week', 1000 * 60 * 60 * 24 * 7],
  ['day', 1000 * 60 * 60 * 24],
  ['hour', 1000 * 60 * 60],
  ['minute', 1000 * 60],
  ['second', 1000],
];

/**
 * "yesterday", "in 3 days", "hace 2 horas" — through `Intl.RelativeTimeFormat`,
 * never hand-written. `numeric: 'auto'` (R3-B5), so a one-day difference says
 * "tomorrow" / "yesterday" rather than "in 1 day".
 */
export function formatRelative(
  iso: string | Date,
  options: { now?: Date; locale?: string; style?: Intl.RelativeTimeFormatStyle } = {},
): string {
  const now = options.now ?? new Date();
  const diffMs = toDate(iso).getTime() - now.getTime();
  const rtf = new Intl.RelativeTimeFormat(activeBcp47(options.locale), { numeric: 'auto', style: options.style ?? 'long' });
  for (const [unit, ms] of RELATIVE_UNITS) {
    if (Math.abs(diffMs) >= ms || unit === 'second') {
      return rtf.format(Math.round(diffMs / ms), unit);
    }
  }
  return rtf.format(0, 'second');
}

/**
 * A counted relative time in one explicit unit, in the compact form a row
 * meta line uses (`lib/utils.ts` `timeAgo`): en keeps its narrow form
 * byte-identical ("3m ago", "2h ago", "1d ago" — `numeric: 'always'`, so a day
 * never turns into "yesterday"), and every other locale uses the `short`
 * style, because CLDR’s fr narrow form reads "-3 min".
 */
export function formatRelativeCompact(
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
  options: { locale?: string } = {},
): string {
  const tag = activeBcp47(options.locale);
  const style: Intl.RelativeTimeFormatStyle = tag === 'en' || tag.startsWith('en-') ? 'narrow' : 'short';
  return new Intl.RelativeTimeFormat(tag, { numeric: 'always', style }).format(value, unit);
}

export interface TimeAgoOptions {
  /** The reference instant; defaults to the current time. Tests pin it. */
  now?: Date;
  /** Override the active locale (declaration code); tests use it. */
  locale?: string;
  /**
   * `'long'` (default): "3 hours ago", "yesterday" — `formatRelative`.
   * `'compact'`: the row-meta form ("just now", "3m ago", "2h ago", "5d ago"),
   * flooring each unit and clamping a future instant (a browser clock behind
   * the server's) to "just now".
   */
  style?: 'long' | 'compact';
  /**
   * `'compact'` only — what an instant a week or more old shows: `'date'`
   * (default) the numeric date ("9/1/2026"), `'relative'` a counted long
   * form, floored: "10 days ago" under 30 days, then months, then years.
   */
  pastWeek?: 'date' | 'relative';
  /** Returned for a missing or unparseable value instead of a throw. Default `''`. */
  fallback?: string;
}

/**
 * THE relative-time formatter (#3168). Every "N ago" label goes through here,
 * in the active UI language. `lib/safe-date.ts` `safeRelative`,
 * `lib/trfer-display.ts` `safeRelativeTime` and `humanizeRelative`, and
 * `lib/utils.ts` `timeAgo` are thin deprecated wrappers over it, kept so no
 * import site broke.
 *
 * A missing or truthy-but-invalid timestamp returns `fallback` rather than
 * throwing, so one malformed backend value cannot blank the row that renders
 * it (B-136 / B-137).
 */
export function formatTimeAgo(iso: string | Date | null | undefined, options: TimeAgoOptions = {}): string {
  const { locale } = options;
  const fallback = options.fallback ?? '';
  if (iso === null || iso === undefined || iso === '') return fallback;
  const d = toDate(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  const now = options.now ?? new Date();
  const seconds = Math.max(0, Math.floor((now.getTime() - d.getTime()) / 1000));
  // Under a minute both styles say "just now": a ticking "20 seconds ago" on a
  // family surface is noise, and the date-fns phrase this replaced never counted seconds.
  if (seconds < 60) return locale ? i18n.t('common:time.justNow', { lng: locale }) : i18n.t('common:time.justNow');
  if (options.style !== 'compact') return formatRelative(d, { now, locale });

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return formatRelativeCompact(-minutes, 'minute', { locale });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return formatRelativeCompact(-hours, 'hour', { locale });
  const days = Math.floor(hours / 24);
  if (days < 7) return formatRelativeCompact(-days, 'day', { locale });
  if (options.pastWeek !== 'relative') return formatNumericDate(d, { locale });
  // Counted and floored, `numeric: 'always'`: "10 days ago", never "last week"
  // (a status line strips the "ago" and needs the count).
  const rtf = new Intl.RelativeTimeFormat(activeBcp47(locale), { numeric: 'always', style: 'long' });
  if (days < 30) return rtf.format(-days, 'day');
  if (days < 365) return rtf.format(-Math.floor(days / 30), 'month');
  return rtf.format(-Math.floor(days / 365), 'year');
}

// ── Date presets (#2109 R3-B5) ────────────────────────────────────────────
//
// One named shape per recurring date-fns pattern, so a lane swaps
// `format(d, 'MMM d')` for `formatMonthDay(d)` and the locale decides the
// order, the month names and the clock. `hour12` is never passed.

/** The `Intl.DateTimeFormat` options behind each preset. `safeFormatIntl` / `dateOnlyIntl` (`lib/safe-date.ts`) take these names. */
export const DATE_PRESETS = {
  /** "Sep 22" — date-fns `MMM d`. */
  monthDay: { month: 'short', day: 'numeric' },
  /** "Sep 22, 2026" — `MMM d, yyyy`. */
  monthDayYear: { month: 'short', day: 'numeric', year: 'numeric' },
  /** "September 22, 2026" — `MMMM d, yyyy`. */
  longDate: { month: 'long', day: 'numeric', year: 'numeric' },
  /** "Tue, Sep 22" — `EEE, MMM d`. */
  weekdayMonthDay: { weekday: 'short', month: 'short', day: 'numeric' },
  /** "September 2026" — `MMMM yyyy`. */
  monthYear: { month: 'long', year: 'numeric' },
  /** "2:30 PM" en, "18:30" fr/pt — `h:mm a`. */
  time: { hour: 'numeric', minute: '2-digit' },
  /** "Tue, 2:30 PM" — `EEE h:mm a`. */
  weekdayTime: { weekday: 'short', hour: 'numeric', minute: '2-digit' },
  /** "Sep 22, 2:30 PM" — `MMM d, h:mm a`. */
  monthDayTime: { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
  /** "9/22/2026" — what a bare `toLocaleDateString()` printed. */
  numeric: { year: 'numeric', month: 'numeric', day: 'numeric' },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;

export type DatePreset = keyof typeof DATE_PRESETS;

export interface PresetOptions {
  /** Override the active locale (declaration code); tests use it. */
  locale?: string;
  /** The zone for an instant. Defaults to the runtime’s own zone. */
  timeZone?: string;
  /**
   * The value is DATE-ONLY (stored as UTC midnight): format it in UTC so it
   * never reads as the previous day west of UTC (B-432). Wins over `timeZone`.
   */
  dateOnly?: boolean;
}

function zoneFor(options: PresetOptions): string {
  return options.dateOnly ? 'UTC' : options.timeZone ?? localTimeZone();
}

/** Format with a named preset or an explicit options object — the one path every preset below goes through. */
export function formatPreset(
  iso: string | Date,
  preset: DatePreset | Intl.DateTimeFormatOptions,
  options: PresetOptions = {},
): string {
  const shape = typeof preset === 'string' ? DATE_PRESETS[preset] : preset;
  return new Intl.DateTimeFormat(activeBcp47(options.locale), { ...shape, timeZone: zoneFor(options) }).format(toDate(iso));
}

/** "Sep 22" · "22 sept" · "9月22日". `month: 'long'` gives "September 22". */
export function formatMonthDay(iso: string | Date, options: PresetOptions & { month?: 'short' | 'long' } = {}): string {
  return formatPreset(iso, { month: options.month ?? 'short', day: 'numeric' }, options);
}

/** "September 2026" · "septiembre de 2026". `month: 'short'` gives "Sep 2026". */
export function formatMonthYear(iso: string | Date, options: PresetOptions & { month?: 'short' | 'long' } = {}): string {
  return formatPreset(iso, { month: options.month ?? 'long', year: 'numeric' }, options);
}

/** "Tue" · "mar" · "周二". `width: 'long'` gives "Tuesday", `'narrow'` gives "T". */
export function formatWeekday(
  iso: string | Date,
  options: PresetOptions & { width?: 'narrow' | 'short' | 'long' } = {},
): string {
  return formatPreset(iso, { weekday: options.width ?? 'short' }, options);
}

/** "2:30 PM" in en, "18:30" in fr and pt — the locale’s own hour cycle (replaces hand-built 12-hour clocks). */
export function formatTime(iso: string | Date, options: PresetOptions = {}): string {
  return formatPreset(iso, 'time', options);
}

/** "Tue, 2:30 PM" · "mar, 18:30". */
export function formatWeekdayTime(iso: string | Date, options: PresetOptions = {}): string {
  return formatPreset(iso, 'weekdayTime', options);
}

/** "9/22/2026" — the twin of a bare `toLocaleDateString()`, with an explicit zone. */
export function formatNumericDate(iso: string | Date, options: PresetOptions = {}): string {
  return formatPreset(iso, 'numeric', options);
}

/**
 * "Sep 1 – 7" · "Sep 28 – Oct 4" · "1–7 sept" — `Intl.DateTimeFormat#formatRange`,
 * which collapses the parts the two ends share. `preset` picks the shape of
 * each end (default `monthDay`); `monthYear` gives "September – October 2026".
 */
export function formatDateRange(
  start: string | Date,
  end: string | Date,
  options: PresetOptions & { preset?: DatePreset | Intl.DateTimeFormatOptions } = {},
): string {
  const preset = options.preset ?? 'monthDay';
  const shape = typeof preset === 'string' ? DATE_PRESETS[preset] : preset;
  return new Intl.DateTimeFormat(activeBcp47(options.locale), { ...shape, timeZone: zoneFor(options) }).formatRange(
    toDate(start),
    toDate(end),
  );
}

/** "a, b, and c" · "a, b y c" · "a、b和c" — `Intl.ListFormat`, never `join(', ')` in copy. */
export function formatList(
  items: readonly string[],
  options: { locale?: string; type?: Intl.ListFormatType; style?: Intl.ListFormatStyle } = {},
): string {
  return new Intl.ListFormat(activeBcp47(options.locale), {
    type: options.type ?? 'conjunction',
    style: options.style ?? 'long',
  }).format(items);
}

/** "1,234.5" · "1.234,5" · "1 234,5". Money never comes here — it goes through `formatMoney`. */
export function formatNumber(value: number, options: Intl.NumberFormatOptions & { locale?: string } = {}): string {
  const { locale, ...format } = options;
  return new Intl.NumberFormat(activeBcp47(locale), format).format(value);
}

/**
 * The first day of the week per declaration code, date-fns style (0 =
 * Sunday, 1 = Monday) — used only when the runtime has no
 * `Intl.Locale#getWeekInfo` (Firefox before 2025). The values are the CLDR
 * week data of each code’s bcp47 tag: en-US and pt-BR start on Sunday.
 */
export const WEEK_START_FALLBACK: Readonly<Record<string, 0 | 1 | 6>> = { en: 0, es: 1, pt: 0, fr: 1, zh: 1 };

type WeekInfo = { firstDay: number };

/**
 * `weekStartsOn` for date-fns and the calendar grids, from the locale’s CLDR
 * week data (`Intl.Locale#getWeekInfo`, or the older `weekInfo` getter). `en`
 * stays Sunday, so no English grid test moves (R3-B5).
 */
export function weekStartFor(locale?: string): 0 | 1 | 2 | 3 | 4 | 5 | 6 {
  const code = locale ?? i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const tag = bcp47For(code);
  try {
    const loc = new Intl.Locale(tag) as Intl.Locale & { getWeekInfo?: () => WeekInfo; weekInfo?: WeekInfo };
    const info = typeof loc.getWeekInfo === 'function' ? loc.getWeekInfo() : loc.weekInfo;
    if (info && typeof info.firstDay === 'number') return (info.firstDay % 7) as 0 | 1 | 2 | 3 | 4 | 5 | 6;
  } catch {
    // An unknown tag: fall through to the table.
  }
  return (WEEK_START_FALLBACK[code] ?? WEEK_START_FALLBACK[code.split('-')[0]] ?? 0) as 0 | 1 | 6;
}
